import React, { useState, useEffect, useRef, useCallback } from "react";
import { Grid, Typography, Button, Box, Slider } from "@mui/material";
import { generator, rotatearray } from "../utils/euclidean";
import RhythmPlayback from "./audiocomponents/RhythmicPlayback";
import AlphariddimsVisualizerCircle from "./visualcomponents/AlphariddimsVisualizerCircle";
import DrumPad from "../pages/DrumPad";

const click = "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/FKI_percussion_clave_01.wav";

// Stable array reference — an inline [click] is a NEW array every render,
// which makes RhythmPlayback re-load (and drop) its audio buffers on every beat.
const SAMPLE_URLS = [click];

const sequences = [
  { id: "Colombia",     name: "Cumbia",                   lat:  4.57,  lng: -74.29, pattern: [4,  3], bpm: 87,  offset: 0, text: "A Colombian folk dance rhythm. 4-beat and 3-beat pattern at 87 BPM — lively and syncopated." },
  { id: "Persia",       name: "Khafif-e-ramal",           lat: 32.0,   lng:  53.0,  pattern: [5,  2], bpm: 100, offset: 2, text: "A Persian rhythm with a 5-beat and 2-beat pattern. Offset of 2 adds a distinctive syncopation." },
  { id: "Bulgaria",     name: "Ruchenitza",               lat: 42.73,  lng:  25.49, pattern: [7,  3], bpm: 100, offset: 0, text: "A Bulgarian folk dance rhythm. 7-beat and 3-beat pattern." },
  { id: "Bulgaria",     name: "Ruchenitza2",              lat: 43.2,   lng:  26.1,  pattern: [7,  4], bpm: 100, offset: 0, text: "A variation of the Bulgarian Ruchenitza with a 7-beat and 4-beat pattern." },
  { id: "Turkey",       name: "Aksak",                    lat: 38.96,  lng:  35.24, pattern: [9,  4], bpm: 100, offset: 0, text: "A Turkish rhythm with an asymmetrical time signature. 9-beat and 4-beat pattern." },
  { id: "Saudi Arabia", name: "York-Samai",               lat: 24.5,   lng:  44.0,  pattern: [6,  5], bpm: 100, offset: 2, text: "A Saudi Arabian rhythm with a 6-beat and 5-beat pattern, offset of 2." },
  { id: "Saudi Arabia", name: "Nawakhat",                 lat: 23.0,   lng:  46.0,  pattern: [7,  5], bpm: 100, offset: 0, text: "A lively Saudi Arabian rhythm with a 7-beat and 5-beat pattern." },
  { id: "Saudi Arabia", name: "Agsag-Samai",              lat: 22.0,   lng:  45.5,  pattern: [9,  5], bpm: 100, offset: 0, text: "A Saudi Arabian rhythm with a 9-beat and 5-beat pattern." },
  { id: "Cuba",         name: "Cuban Tresillo",           lat: 22.0,   lng: -79.5,  pattern: [8,  3], bpm: 100, offset: 0, text: "A Cuban rhythm with an infectious groove. 8-beat and 3-beat pattern." },
  { id: "Cuba",         name: "Cuban Cinquillo",          lat: 21.0,   lng: -80.5,  pattern: [8,  5], bpm: 100, offset: 0, text: "A lively Cuban rhythm with an 8-beat and 5-beat pattern." },
  { id: "Spain",        name: "Spanish Tango",            lat: 40.46,  lng:  -3.75, pattern: [8,  5], bpm: 80,  offset: 2, text: "A passionate Spanish rhythm. 8-beat and 5-beat pattern at 80 BPM." },
  { id: "Russia",       name: "Pictures at an Exhibtion", lat: 55.75,  lng:  37.6,  pattern: [11, 5], bpm: 100, offset: 0, text: "A complex Russian rhythm with an 11-beat and 5-beat pattern." },
  { id: "Africa",       name: "Venda",                    lat: -22.0,  lng:  30.0,  pattern: [12, 5], bpm: 100, offset: 0, text: "An African rhythm with a 12-beat and 5-beat pattern." },
  { id: "Brazil",       name: "Bossa Nova",               lat: -15.0,  lng: -47.0,  pattern: [16, 5], bpm: 80,  offset: 6, text: "A smooth Brazilian rhythm. 16-beat and 5-beat pattern at 80 BPM." },
];

// ── Map — CDN Leaflet, same pattern as DrumTalk ───────────────────────────
const MapPanel = ({ activeIdx, onSelect }) => {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef([]);
  const [leafletLoaded, setLeafletLoaded] = useState(false);

  useEffect(() => {
    if (window.L) { setLeafletLoaded(true); return; }
    const link  = document.createElement("link");
    link.rel    = "stylesheet";
    link.href   = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(link);
    const script  = document.createElement("script");
    script.src    = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => setLeafletLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return;
    const L   = window.L;
    const map = L.map(mapRef.current, { center: [20, 15], zoom: 2, scrollWheelZoom: false });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_42ii_1_2cf9e03592ae4317b455a173", {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>', maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    // Force Leaflet to recalculate after the container is painted
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [leafletLoaded]);

  useEffect(() => {
    if (!mapInstanceRef.current || !leafletLoaded) return;
    const L   = window.L;
    const map = mapInstanceRef.current;

    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    sequences.forEach((seq, idx) => {
      const isActive = idx === activeIdx;
      const icon = L.divIcon({
        html: `<div style="
          width:${isActive ? 40 : 28}px; height:${isActive ? 40 : 28}px;
          border-radius:50%;
          background:${isActive ? "#F5A623" : "#4aabc5"};
          border:2.5px solid white;
          display:flex; align-items:center; justify-content:center;
          font-size:${isActive ? 18 : 13}px;
          cursor:pointer;
          box-shadow:0 0 0 ${isActive ? "4px rgba(245,166,35,0.4)" : "3px rgba(74,171,197,0.3)"},0 4px 10px rgba(0,0,0,0.5);
        ">🥁</div>`,
        className: "", iconSize: [isActive ? 40 : 28, isActive ? 40 : 28], iconAnchor: [isActive ? 20 : 14, isActive ? 40 : 28],
      });
      const marker = L.marker([seq.lat, seq.lng], { icon }).addTo(map);
      marker.on("click", (e) => { onSelect(idx); e.originalEvent.stopPropagation(); });
      markersRef.current.push(marker);
    });

    if (activeIdx !== null) {
      const s = sequences[activeIdx];
      map.flyTo([s.lat, s.lng], 4, { duration: 1.0 });
    }
  }, [leafletLoaded, activeIdx]);

return (
  <Box
    ref={mapRef}
    sx={{ width: "100%", height: "520px", borderRadius: "16px", border: "2px solid #333" }}
  />
);
};

// ── Main ──────────────────────────────────────────────────────────────────
const EuclideanSequencer_Buttons = () => {
  const [sequence,         setSequence]         = useState([]);
  const [isPlaying,        setIsPlaying]        = useState(false);
  const [bpm,              setBpm]              = useState(120);
  const [selectedSequence, setSelectedSequence] = useState(0);
  const [currentBeat,      setCurrentBeat]      = useState(0);
  const [loop,             setLoop]             = useState(false);
  const currentBeatRef = useRef(0);

  const handleSequenceChange = (idx) => {
    const i = Number(idx);
    setSelectedSequence(i);
    setSequence(rotatearray(generator(sequences[i].pattern[0], sequences[i].pattern[1]), sequences[i].offset));
    setBpm(sequences[i].bpm);
    setIsPlaying(false);
  };

  useEffect(() => {
    // Init first sequence
    handleSequenceChange(0);
  }, []);

  // Don't regenerate the sequence when Start is pressed — handleSequenceChange
  // already did. Replacing the array here gave RhythmPlayback a new `sequence`
  // prop at the exact moment playback began, which can reset/stop it.
  useEffect(() => {
    if (isPlaying) {
      currentBeatRef.current = 0;
      setCurrentBeat(0);
    }
  }, [isPlaying]);

  // Stable callbacks so RhythmPlayback's effects don't tear down on every render.
  const handleBeatChange = useCallback((beat) => {
    setCurrentBeat(beat);
    currentBeatRef.current = beat;
  }, []);

  const handleReset = useCallback(() => setIsPlaying(false), []);

  const currentSeq = sequences[selectedSequence];

  return (
    <Grid container spacing={2} alignItems="flex-start" justifyContent="center"
      sx={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>

      {/* Title */}
      <Grid item xs={12} sx={{ textAlign: "center" }}>
        <Typography sx={{ color: "#ffffff" }} variant="h6">Euclidean Rhythm Generator</Typography>
      </Grid>

      {/* Buttons */}
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
        {sequences.map((seq, idx) => (
          <Button
            key={idx}
            variant={selectedSequence === idx ? "contained" : "outlined"}
            onClick={() => handleSequenceChange(idx)}
            sx={{ textTransform: "none" }}
          >
            {seq.name}
          </Button>
        ))}
      </Grid>

{/* Map + Rhythm Layout */}

<div style={{ display: "flex", gap: 16, alignItems: "flex-start", width: "100%", flexWrap: "wrap" }}>

  {/* MAP — 2/3 width */}
<div style={{ flex: "1 1 420px", minWidth: 0 }}>
    <MapPanel
      activeIdx={selectedSequence}
      onSelect={handleSequenceChange}
    />
  </div>

  {/* VISUALIZER — 1/3 width */}
<div style={{ flex: "1 1 260px", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
        }}
      >

        {/* Circle Visualization */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            aspectRatio: "1 / 1",
            p: 2,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <AlphariddimsVisualizerCircle
            sequence={sequence}
            letter={currentSeq.name}
            currentBeat={currentBeat}
          />
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: 2,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color={isPlaying ? "secondary" : "primary"}
              onClick={() => setIsPlaying((p) => !p)}
            >
              {isPlaying ? "Stop" : "Start"}
            </Button>

            <Button
              variant="contained"
              color={loop ? "secondary" : "primary"}
              onClick={() => setLoop((p) => !p)}
            >
              {loop ? "Loop" : "OneShot"}
            </Button>
          </Box>

          {/* BPM Slider */}
          <Box sx={{ mt: 2, px: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                BPM
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "#F5A623" }}>
                {bpm}
              </Typography>
            </Box>
            <Slider
              value={bpm}
              min={40}
              max={240}
              step={1}
              onChange={(_, val) => setBpm(val)}
              sx={{
                color: "#F5A623",
                "& .MuiSlider-thumb": {
                  width: 16,
                  height: 16,
                  bgcolor: "#F5A623",
                  "&:hover": { boxShadow: "0 0 0 8px rgba(245,166,35,0.2)" },
                },
                "& .MuiSlider-track": { bgcolor: "#F5A623", border: "none" },
                "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)" }}>40</Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.35)" }}>240</Typography>
            </Box>
          </Box>
        </Box>

        {/* Description */}
        <Box
          sx={{
            p: 2,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.70rem",
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#F5A623",
              }}
            >
              {currentSeq.id}
            </Typography>

            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "#fff",
              }}
            >
              {currentSeq.name}
            </Typography>

            <Box
              sx={{
                ml: "auto",
                display: "flex",
                gap: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                {currentSeq.pattern[0]}×{currentSeq.pattern[1]}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.65rem",
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: "rgba(245,166,35,0.2)",
                  color: "#F5A623",
                }}
              >
                {currentSeq.bpm} BPM
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: "0.84rem",
              lineHeight: 1.65,
              color: "rgba(255,255,255,0.7)",
            }}
          >
            {currentSeq.text}
          </Typography>
        </Box>

      </Box>
      </div>
  </div>

      {/* Drum Pad */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <DrumPad />
        </Box>
      </Grid>

      <RhythmPlayback
        sequence={sequence}
        bpm={bpm}
        isPlaying={isPlaying}
        onCurrentBeatChange={handleBeatChange}
        reset_playback={handleReset}
        sampleUrls={SAMPLE_URLS}
        loop={loop}
      />
    </Grid>
  );
};

export default EuclideanSequencer_Buttons;
